import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Plus, Minus, ArrowUpRight } from "lucide-react";
import { CATEGORIES, TOTAL_SERVICES } from "@/data/services";
import { scrollToId } from "@/lib/scroll";

export const ServiceChapters = () => {
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState("01");
  const q = query.trim().toLowerCase();

  const visible = useMemo(() => {
    if (!q) return CATEGORIES;
    return CATEGORIES.map((c) => ({
      ...c,
      services: c.services.filter((s) => s.toLowerCase().includes(q)),
    })).filter((c) => c.services.length > 0 || c.name.toLowerCase().includes(q));
  }, [q]);

  return (
    <section id="services" data-testid="services-section" className="relative py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="max-w-3xl"
        >
          <p className="font-mono text-xs tracking-[0.3em] text-cyan-400 uppercase font-semibold">
            02 // The Manifesto
          </p>
          <h2 className="mt-4 font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-slate-100">
            Twelve Chapters. <span className="metallic-text">{TOTAL_SERVICES} Services.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 leading-relaxed">
            One partner for every layer of your technology stack — open a chapter to explore
            its full service index.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-10 relative max-w-xl"
        >
          <Search size={16} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="service-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search all ${TOTAL_SERVICES} services — try "firewall", "payroll", "cctv"...`}
            className="w-full bg-ark-800/70 border border-white/10 rounded-full pl-12 pr-5 py-4 text-sm text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50 focus:shadow-[0_0_25px_rgba(56,189,248,0.12)] transition-all duration-300"
          />
        </motion.div>

        <div className="mt-12 border-t border-white/10">
          {visible.map((cat, idx) => {
            const open = q ? true : openId === cat.id;
            return (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: Math.min(idx * 0.05, 0.3) }}
                className="border-b border-white/10"
              >
                <button
                  data-testid={`category-tab-${cat.id}`}
                  onClick={() => setOpenId(open ? null : cat.id)}
                  className="w-full group flex items-center gap-6 sm:gap-10 py-7 text-left"
                >
                  <span className="font-mono text-sm text-cyan-400/80 shrink-0 w-8">
                    {cat.id}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span
                      className={`block font-display font-semibold text-lg sm:text-2xl tracking-tight transition-colors duration-300 ${
                        open ? "text-white" : "text-slate-400 group-hover:text-slate-100"
                      }`}
                    >
                      {cat.name}
                    </span>
                    <span className="block mt-1 text-xs text-slate-500 truncate">
                      {cat.tagline}
                    </span>
                  </span>
                  <span className="hidden sm:block font-mono text-[11px] text-slate-500 shrink-0">
                    {cat.services.length} SERVICES
                  </span>
                  <span
                    className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${
                      open
                        ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300"
                        : "border-white/15 text-slate-400 group-hover:border-cyan-400/40 group-hover:text-cyan-300"
                    }`}
                  >
                    {open ? <Minus size={15} /> : <Plus size={15} />}
                  </span>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-9 pl-14 sm:pl-[4.5rem] pr-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                        {cat.services.map((s, i) => (
                          <button
                            key={s}
                            data-testid={`service-item-${cat.id}-${i}`}
                            onClick={() => scrollToId("contact")}
                            className="group/item flex items-start gap-2.5 text-left text-sm text-slate-400 hover:text-cyan-200 transition-colors duration-200 py-0.5"
                          >
                            <ArrowUpRight
                              size={13}
                              className="mt-1 shrink-0 text-cyan-500/50 group-hover/item:text-cyan-300 group-hover/item:translate-x-0.5 group-hover/item:-translate-y-0.5 transition-all"
                            />
                            {s}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {visible.length === 0 && (
          <p data-testid="service-search-empty" className="py-16 text-center text-slate-500 text-sm">
            No services match "{query}" — try a broader term or call us directly.
          </p>
        )}
      </div>
    </section>
  );
};
